import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { IFeed, PollContent } from "@/types/IFeed";

const initialState: IFeed[] = [];

export const PostSlice = createSlice({
  name: "posts_slice",
  initialState,
  reducers: {
    setFeedData: (state, action: PayloadAction<IFeed[]>) => {
      if (action.payload.length === 0) {
        return [];
      } else {
        const ids = new Set(state.map((d) => d.id));
        const merged = [
          ...state,
          ...action.payload.filter((d) => !ids.has(d.id)),
        ];
        return merged;
      }
    },
    insertFeed: (state, action: PayloadAction<IFeed>) => {
      if (!action.payload.repostedContent) {
        return [action.payload, ...state];
      } else return;
    },
    deletePosts: (state, action: PayloadAction<any>) => {
      const updated = state.filter((feed) => {
        return !action.payload.data.includes(feed.id);
      });
      return updated;
    },
    updateFollow: (state, action: PayloadAction<number>) => {
      const updated = state.map((feed) => {
        if (feed.writer === action.payload) {
          return { ...feed, followCheck: feed.followCheck === 1 ? 0 : 1 };
        }
        if (
          feed.repostedContent &&
          feed.repostedContent.writer === action.payload
        ) {
          const updatedRepostedContent = {
            ...feed.repostedContent,
            followCheck: feed.repostedContent.followCheck === 1 ? 0 : 1,
          };
          return { ...feed, repostedContent: updatedRepostedContent };
        }
        return feed;
      });
      return updated;
    },
    muteBlock: (state, action: PayloadAction<number>) => {
      const updated = state.filter((feed) => {
        return feed.writer !== action.payload;
      });
      return updated;
    },
    deleteRepost: (state, action: PayloadAction<any>) => {
      const updatedState = state.map((feed) => {
        let updatedFeed = { ...feed };

        if (
          feed.id === action.payload.repostedContent?.id ||
          feed.repostedContent?.id === action.payload.repostedContent?.id
        ) {
          updatedFeed.repostedByMe = updatedFeed.repostedByMe === 0 ? 1 : 0;
          updatedFeed.amountReposts = Math.max(
            0,
            updatedFeed.amountReposts - 1
          );
          updatedFeed.amountV2Reposts = Math.max(
            0,
            updatedFeed.amountV2Reposts - 1
          );

          if (updatedFeed.repostedContent) {
            const updatedRepostedContent = { ...updatedFeed.repostedContent };
            updatedRepostedContent.repostedByMe =
              updatedRepostedContent.repostedByMe === 0 ? 1 : 0;
            updatedRepostedContent.amountReposts = Math.max(
              0,
              updatedRepostedContent.amountReposts - 1
            );
            updatedRepostedContent.amountV2Reposts = Math.max(
              0,
              updatedRepostedContent.amountV2Reposts - 1
            );
            updatedFeed.repostedContent = updatedRepostedContent;
          }
        }

        return updatedFeed;
      });

      const newState = updatedState.filter(
        (feed) => feed.id !== action.payload.id
      );
      return newState;
    },
    repost: (state, action: PayloadAction<any>) => {
      const updatedState = state.map((feed) => {
        if (
          feed.id === action.payload.repostedContent?.id ||
          (feed.repostedContent?.id === action.payload.repostedContent?.id &&
            feed.textContent === "" &&
            feed.attachments.length === 0)
        ) {
          const updatedFeed = { ...feed }; // Create a shallow copy to modify
          updatedFeed.repostedByMe = updatedFeed.repostedByMe === 0 ? 1 : 0;
          updatedFeed.amountReposts += 1;
          updatedFeed.amountV2Reposts += 1;

          if (updatedFeed.repostedContent) {
            const updatedRepostedContent = { ...updatedFeed.repostedContent }; // Create a shallow copy
            updatedRepostedContent.repostedByMe =
              updatedRepostedContent.repostedByMe === 0 ? 1 : 0;
            updatedRepostedContent.amountReposts += 1;
            updatedRepostedContent.amountV2Reposts += 1;
            updatedFeed.repostedContent = updatedRepostedContent;
          }

          return updatedFeed;
        }

        return feed; // If the feed doesn't need to be updated, return it unchanged
      });

      return updatedState;
    },
    quote: (state, action: PayloadAction<any>) => {
      const updatedState = state.map((feed) => {
        if (feed.id === action.payload.postId) {
          return { ...feed, amountV2Quotes: feed.amountV2Quotes + 1 };
        }
        return feed;
      });
      return updatedState;
    },
    likeUnlikePost: (state, action: PayloadAction<number>) => {
      state.filter((feed) => {
        if (feed.id === action.payload) {
          feed.liked = feed.liked === 1 ? 0 : 1;
          feed.amountLikes =
            feed.liked === 1
              ? feed.amountLikes + 1
              : feed.amountLikes === 0
                ? 0
                : feed.amountLikes - 1;
        }
        if (
          feed.repostedContent &&
          feed.textContent === "" &&
          feed.repostedContent.id === action.payload
        ) {
          feed.liked = feed.liked === 1 ? 0 : 1;
          feed.amountLikes =
            feed.liked === 1
              ? feed.amountLikes + 1
              : feed.amountLikes === 0
                ? 0
                : feed.amountLikes - 1;
          feed.repostedContent.liked = feed.repostedContent.liked === 1 ? 0 : 1;
          feed.repostedContent.amountLikes =
            feed.repostedContent.liked === 1
              ? feed.repostedContent.amountLikes + 1
              : feed.repostedContent.amountLikes === 0
                ? 0
                : feed.repostedContent.amountLikes - 1;
        }
        return feed;
      });
    },
    updateComments: (
      state,
      action: PayloadAction<{ amountReplies: number; id: number }>
    ) => {
      const updated = state.map((feed) => {
        if (feed.id === action.payload.id) {
          const updatedFeed = {
            ...feed,
            amountReplies: action.payload.amountReplies,
          };
          return updatedFeed;
        }
        return feed;
      });
      return updated;
    },
    bookMarkPosts: (state, action: PayloadAction<number>) => {
      state.filter((feed) => {
        if (feed.id === action.payload) {
          feed.bookmarked = feed.bookmarked === 1 ? 0 : 1;
        }
        if (
          feed.repostedContent &&
          feed.textContent === "" &&
          feed.repostedContent.id === action.payload
        ) {
          feed.bookmarked = feed.bookmarked === 1 ? 0 : 1;
          feed.repostedContent.bookmarked =
            feed.repostedContent.bookmarked === 1 ? 0 : 1;
        }
        return feed;
      });
    },
    updatePollsPost: (
      state,
      action: PayloadAction<{ postId: number; data: PollContent }>
    ) => {
      state.filter((feed) => {
        if (feed.id === action.payload.postId) {
          feed.pollContent = action.payload.data;
        }
        if (
          feed.repostedContent &&
          feed.repostedContent.id === action.payload.postId
        ) {
          feed.repostedContent.pollContent = action.payload.data;
        }
        return feed;
      });
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setFeedData,
  likeUnlikePost,
  repost,
  quote,
  deleteRepost,
  muteBlock,
  updateFollow,
  deletePosts,
  insertFeed,
  updateComments,
  bookMarkPosts,
  updatePollsPost,
} = PostSlice.actions;

export default PostSlice.reducer;
